import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { fDate } from '../util/format-time';

import Iconify from '../../theme/iconify/iconify';
import SvgColor from '../../theme/svg-color/svg-color';

function DocumentCard({ document, inCorpus, removeDocument }) {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();

    const renderAvatar = (
        <Avatar
            id='mISLec_avatar_icon'
            alt='PDF icon'
            src='/assets/icons/pdf.png'
            sx={{
                zIndex: 9,
                width: 32,
                height: 32,
                position: 'absolute',
                left: 27,
                bottom: -18
            }}
        />
    );

    const renderTitle = (
        <Link
            color='inherit'
            variant='subtitle2'
            underline='hover'
            onClick={() => navigate(`/documents/${document._id}`)}
            sx={{
                height: 44,
                overflow: 'hidden',
                WebkitLineClamp: 2,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical'
            }}
        >
            {document.filename}
        </Link>
    );

    const renderInfo = (
        <Stack
            direction='row'
            flexWrap='wrap'
            spacing={1.5}
            alignItems='center'
            justifyContent={inCorpus ? 'space-between' : 'flex-end'}
            sx={{
                mt: 2,
                color: 'text.disabled',
            }}
        >
            {inCorpus && (
                <Tooltip title={t('documents.card.tooltips.removeFromCorpus')} arrow>
                    <IconButton
                        color='error'
                        onClick={() => removeDocument(document._id)}
                        sx={{ p: 1 }}
                    >
                        <Iconify width={16} icon='eva:trash-outline' sx={{ color: 'error.main' }} />
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title={t('documents.card.tooltips.size')} arrow>
                <Stack
                    direction='row'
                    sx={{
                        p: 0.5,
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: 'rgba(145, 158, 171, 0.08)',
                        },
                    }}
                >
                    <Iconify width={16} icon='eva:attach-2-fill' sx={{ mr: 0.5 }} />
                    <Typography variant='caption'>{(document.size / (1024 * 1000)).toFixed(2)} KB</Typography>
                </Stack>
            </Tooltip>
        </Stack>
    );

    const renderDate = (
        <Grid container>
            <Grid item xs={inCorpus ? 10 : 12}>
                <Typography
                    variant='caption'
                    component='div'
                    sx={{
                        mb: 2,
                        color: 'text.disabled'
                    }}
                >
                    {fDate(document.timestamp, i18n.language)}
                </Typography>
            </Grid>
            {inCorpus && !document.embed && (
                <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                    <Tooltip title={t('corpus.tooltips.notEmbedded')} enterTouchDelay={0} arrow>
                        <Iconify width={16} icon='icomoon-free:embed2' sx={{ color: 'warning.main' }} />
                    </Tooltip>
                </Grid>
            )}
        </Grid>
    );

    const renderShape = (
        <SvgColor
            color='paper'
            src='/assets/icons/shape-avatar.svg'
            sx={{
                width: 80,
                height: 36,
                zIndex: 9,
                left: 3,
                bottom: -16,
                position: 'absolute',
                color: 'background.paper',
                borderRadius: '16px'
            }}
        />
    );

    return (
        <Grid item xs={12} sm={6} md={3} sx={{ mb: 1 }}>
            <Paper elevation={2}>
                <Box sx={{ position: 'relative' }}>
                    {renderShape}
                    {renderAvatar}
                </Box>

                <Box
                    sx={{
                        p: (theme) => theme.spacing(4, 3, 2, 3),
                        backgroundColor: 'white',
                        borderRadius: '16px'
                    }}
                >
                    {renderDate}
                    {renderTitle}
                    {renderInfo}
                </Box>
            </Paper>
        </Grid>
    );
}

DocumentCard.propTypes = {
    document: PropTypes.object.isRequired
};

export default DocumentCard;